import React, { useEffect, useState } from 'react';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import { formatCurrency } from '../utils/currency';
import api from '../utils/api';
import './LoanIncreaseRequestList.css';

const LoanIncreaseRequestList = ({ loanId }) => {
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loanId) {
      fetchRequests();
    }
  }, [loanId]);

  const fetchRequests = async () => {
    try {
      const response = await api.get(`/ctv/loan-increase-requests/loan/${loanId}`);
      setRequests(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch increase requests:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-text">{t('loading')}</div>;
  }

  if (requests.length === 0) {
    return null;
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} className="status-icon pending" />;
      case 'approved':
        return <CheckCircle size={16} className="status-icon approved" />;
      case 'rejected':
        return <XCircle size={16} className="status-icon rejected" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return t('statusPending');
      case 'approved':
        return t('statusApproved');
      case 'rejected':
        return t('statusRejected');
      default:
        return status;
    }
  };

  const getStatusClass = (status) => {
    return `status-badge ${status}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="increase-requests-section">
      <h3>{t('increaseRequestHistory')}</h3>
      <div className="requests-list">
        {requests.map((request) => (
          <div key={request.id} className={`request-card ${request.status}`}>
            <div className="request-header">
              <span className={getStatusClass(request.status)}>
                {getStatusIcon(request.status)}
                {getStatusText(request.status)}
              </span>
              <span className="request-date">{formatDate(request.createdAt)}</span>
            </div>

            <div className="request-amounts">
              <div className="amount-item">
                <label>{t('currentAmountLabel')}:</label>
                <span>{formatCurrency(request.currentAmount)}</span>
              </div>
              <div className="amount-arrow">→</div>
              <div className="amount-item highlight">
                <label>{t('requestedAmountLabel')}:</label>
                <span>{formatCurrency(request.requestedAmount)}</span>
              </div>
              <div className="amount-item increase">
                <label>{t('increaseLabel')}:</label>
                <span className="increase-value">
                  +{formatCurrency(request.increaseAmount)}
                </span>
              </div>
            </div>

            {request.reason && (
              <div className="request-reason">
                <label>{t('reasonLabel')}:</label>
                <p>{request.reason}</p>
              </div>
            )}

            {request.status === 'rejected' && request.rejectionReason && (
              <div className="rejection-reason">
                <label>{t('rejectionReasonLabel')}:</label>
                <p>{request.rejectionReason}</p>
              </div>
            )}

            {request.status === 'approved' && request.approvedAt && (
              <div className="approval-info">
                <small>{t('approvedAtLabel')}: {formatDate(request.approvedAt)}</small>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LoanIncreaseRequestList;
